import { useState, useMemo } from 'react';
import { FiSearch, FiAlertCircle } from 'react-icons/fi';
import { toast } from 'react-toastify';
import appConfig from '../../../config/app-config';
import useFetch from '../../../hooks/useFetch';
import ControlEditWithIcon from '../../molecules/ControlEditWithIcon';
import TextEdit from '../../molecules/TextEdit';
import {
    formatCurrency,
    calculateIntensity,
    getIntensityColor,
    type GuaranteeItem,
    type OperationType,
    type DocStatus,
} from './collateralUtils';
import { collateralApi } from '../../../services/collateralApi';
import { getGuarantees } from './collateralStore';

const API = appConfig.apis.convertXmlApiEnv;

interface OperationOption {
    id: string;
    operationCode: string;
    assignorName: string;
    assignorCnpj: string;
    status: string;
    totalDocuments: number;
    totalReceivables: number;
    totalAmount: number;
    operationType: OperationType;
    obligation: string;
    traderName: string;
}

interface Props {
    onClose: () => void;
    onCreated: () => void;
}

const DOC_OPTIONS: { key: string; label: string; weight: number }[] = [
    { key: 'xml', label: 'XML', weight: 1 },
    { key: 'nfPdf', label: 'NF PDF', weight: 2 },
    { key: 'canhoto', label: 'Canhoto', weight: 4 },
    { key: 'duplicata', label: 'Duplicata c/ Aceite', weight: 5 },
];

const NewAnalysisFromOperation = ({ onClose, onCreated }: Props) => {
    const [step, setStep] = useState<'select' | 'configure'>('select');
    const [selected, setSelected] = useState<OperationOption | null>(null);
    const [search, setSearch] = useState('');

    // Config fields
    const [requiredDocs, setRequiredDocs] = useState<Record<string, boolean>>({ xml: true, nfPdf: false, canhoto: false, duplicata: false });
    const [callValidation, setCallValidation] = useState(false);
    const [purchaseContract, setPurchaseContract] = useState(false);
    const [sefaz, setSefaz] = useState(false);
    const [analysisCriteria, setAnalysisCriteria] = useState<'Completa' | 'Amostragem'>('Amostragem');
    const [sampledTitles, setSampledTitles] = useState(1);

    // Fetch operations from convert_xml API
    const fetchHeaders = useMemo(() => [
        { key: 'x-api-key', value: API.key || '' },
        { key: 'Accept-Charset', value: 'utf-8' },
    ], []);
    const url = `${API.address}/operations?page=0&pageSize=50`;
    const { isLoading, data } = useFetch(url, fetchHeaders);

    const operations: OperationOption[] = useMemo(() => {
        if (!data?.list) return [];
        return data.list;
    }, [data]);

    // Filter out operations that already have a guarantee analysis
    const existingCodes = useMemo(() => new Set(getGuarantees().map(g => g.operationCode)), []);

    const filteredOps = useMemo(() => {
        let ops = operations.filter(o => !existingCodes.has(o.operationCode));
        if (search) {
            const s = search.toLowerCase();
            ops = ops.filter(o => o.operationCode?.toLowerCase().includes(s) || o.assignorName?.toLowerCase().includes(s));
        }
        return ops;
    }, [operations, existingCodes, search]);

    // Intensity calculation
    const intensity = useMemo(() => {
        if (!selected) return null;
        return calculateIntensity(
            { xml: requiredDocs.xml, nf: requiredDocs.nfPdf, canhoto: requiredDocs.canhoto, duplicata: requiredDocs.duplicata },
            selected.totalReceivables || 0
        );
    }, [selected, requiredDocs]);

    const handleCreate = async () => {
        if (!selected) return;

        try {
            // Fetch documents from convert-xml
            const fetchHeaders: Record<string, string> = { 'x-api-key': API.key || '' };
            const session = await (await import('aws-amplify')).Auth.currentSession();
            fetchHeaders['Authorization'] = `Bearer ${session.getIdToken().getJwtToken()}`;

            const opRes = await fetch(`${API.address}/operations/${selected.id}`, { headers: fetchHeaders });
            const opData = await opRes.json();
            const documents = opData?.documents || [];

            // Populate receivables via collateral API
            // First, wait a moment for the event-driven analysis creation
            // Then find the analysis by operation code
            const listRes = await collateralApi.getGuarantees({ search: selected.operationCode, pageSize: 1 });
            const analysis = listRes?.items?.[0];

            if (analysis) {
                await collateralApi.populateReceivables(analysis.id, documents);
            }

            onCreated();
        } catch (e) {
            console.error('Error creating analysis:', e);
            toast.error('Erro ao criar análise. Tente novamente.');
        }
    };

    // Step 1: Select operation
    if (step === 'select') {
        return (
            <div>
                <div className="flex items-center justify-between mb-6">
                    <h3 className="text-lg font-medium text-gray-900 dark:text-white">Nova Análise de Garantia</h3>
                    <div className="flex items-center gap-3">
                        <button onClick={onClose} className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 dark:bg-slate-700 dark:text-gray-300 text-sm">Cancelar</button>
                        <button onClick={() => selected && setStep('configure')} disabled={!selected}
                            className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:opacity-50 text-sm">Próximo</button>
                    </div>
                </div>

                <div className="relative mb-3">
                    <FiSearch className="absolute left-3 top-2.5 text-gray-400" size={16} />
                    <input type="text" placeholder="Buscar por código ou cedente..." value={search} onChange={e => setSearch(e.target.value)}
                        className="w-full pl-9 pr-3 py-2 border border-gray-300 dark:border-slate-600 rounded-md text-sm bg-white dark:bg-slate-700 dark:text-white" />
                </div>

                {isLoading ? (
                    <div className="text-center py-8 text-gray-500">Carregando operações...</div>
                ) : (
                    <div className="space-y-2">
                        {filteredOps.length === 0 ? (
                            <div className="text-center py-8 text-gray-500 dark:text-gray-400">Nenhuma operação disponível</div>
                        ) : filteredOps.map(op => (
                            <div key={op.id}
                                onClick={() => setSelected(op)}
                                className={`p-3 rounded-lg border transition-colors cursor-pointer ${selected?.id === op.id
                                    ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/30 dark:border-blue-400'
                                    : 'border-gray-200 dark:border-slate-700 hover:border-blue-300 dark:hover:border-blue-600'
                                    }`}>
                                <div className="flex items-center justify-between">
                                    <div>
                                        <div className="flex items-center gap-2">
                                            <span className="font-medium text-sm dark:text-white">{op.operationCode}</span>
                                            <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${op.status === 'closed' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
                                                {op.status === 'closed' ? 'Fechado' : 'Aberto'}
                                            </span>
                                        </div>
                                        <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">{op.assignorName}</div>
                                    </div>
                                    <div className="text-right">
                                        <div className="text-sm font-medium dark:text-white">{formatCurrency(op.totalAmount || 0)}</div>
                                        <div className="text-xs text-gray-500 dark:text-gray-400">{op.totalReceivables || 0} títulos · {op.totalDocuments || 0} docs</div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        );
    }

    const totalTitles = selected!.totalReceivables || 1;
    const samplePercentage = totalTitles > 0 ? ((sampledTitles / totalTitles) * 100) : 0;

    // Step 2: Configure analysis
    return (
        <div>
            <div className="flex items-center justify-between mb-6">
                <div>
                    <h3 className="text-lg font-medium text-gray-900 dark:text-white">Configurar Análise</h3>
                </div>
                <button onClick={handleCreate} className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 text-sm flex-shrink-0 whitespace-nowrap">
                    Criar Análise
                </button>
            </div>

            <div className="space-y-4">
                <div className="bg-gray-50 dark:bg-slate-900 rounded-lg p-3 text-sm">
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                        {selected!.operationCode} — {selected!.assignorName}
                    </p>
                    <div className="text-gray-500 dark:text-gray-400">{formatCurrency(selected!.totalAmount || 0)} · {selected!.totalReceivables || 0} títulos</div>
                </div>

                {/* Operation type, obligation & trader (read-only from structuring) */}
                <div className="grid grid-cols-2 gap-3">
                    <TextEdit label="Tipo Operação" value={selected!.operationType || 'CESSÃO'} disabled />
                    <TextEdit label="Obrigação" value={selected!.obligation || 'SEM REGRESSO'} disabled />
                </div>
                <TextEdit label="Trader" value={selected!.traderName || '—'} disabled />

                {/* Required documents */}
                <div>
                    <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-2">Documentos Obrigatórios</label>
                    <div className="grid grid-cols-2 gap-2">
                        {DOC_OPTIONS.map(doc => (
                            <label key={doc.key} className="flex items-center gap-2 text-sm dark:text-white cursor-pointer">
                                <input type="checkbox" checked={requiredDocs[doc.key]} onChange={e => setRequiredDocs(prev => ({ ...prev, [doc.key]: e.target.checked }))} className="rounded" />
                                {doc.label} <span className="text-xs text-gray-400">(peso {doc.weight})</span>
                            </label>
                        ))}
                        <label className="flex items-center gap-2 text-sm dark:text-white cursor-pointer">
                            <input type="checkbox" checked={callValidation} onChange={e => setCallValidation(e.target.checked)} className="rounded" />
                            Validação Ligação/Email
                        </label>
                        <label className="flex items-center gap-2 text-sm dark:text-white cursor-pointer">
                            <input type="checkbox" checked={purchaseContract} onChange={e => setPurchaseContract(e.target.checked)} className="rounded" />
                            Contrato Compra/Venda
                        </label>
                        <label className="flex items-center gap-2 text-sm dark:text-white cursor-pointer">
                            <input type="checkbox" checked={sefaz} onChange={e => setSefaz(e.target.checked)} className="rounded" />
                            Validação SEFAZ
                        </label>
                    </div>
                </div>

                {/* Intensity preview */}
                {intensity && (
                    <div className="flex items-center gap-3 bg-gray-50 dark:bg-slate-900 rounded-lg p-3">
                        <span className="text-sm text-gray-600 dark:text-gray-400">Intensidade calculada:</span>
                        <span className={`px-3 py-1 rounded-full text-sm font-medium ${getIntensityColor(intensity.level)}`}>
                            {intensity.level} (score: {intensity.score})
                        </span>
                    </div>
                )}

                {/* Criteria */}
                <div className="flex items-center gap-6">
                    <div>
                        <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Critério Análise</label>
                        <div className="flex items-center gap-4">
                            {(['Completa', 'Amostragem'] as const).map(opt => (
                                <label key={opt} className="flex items-center gap-2 cursor-pointer">
                                    <input type="radio" value={opt} checked={analysisCriteria === opt} onChange={() => setAnalysisCriteria(opt)}
                                        className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500" />
                                    <span className="text-sm text-gray-900 dark:text-gray-100">{opt}</span>
                                </label>
                            ))}
                        </div>
                    </div>

                    {analysisCriteria === 'Amostragem' && (
                        <div className="flex items-center gap-2" style={{ transform: 'scale(0.85)', transformOrigin: 'left center' }}>
                            <div style={{ minWidth: '11rem' }}>
                                <ControlEditWithIcon
                                    label=""
                                    text="títulos"
                                    minValue={1}
                                    maxValue={totalTitles}
                                    value={sampledTitles}
                                    onIncrement={setSampledTitles}
                                    onDecrement={setSampledTitles}
                                />
                            </div>
                            <span className="text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">
                                {samplePercentage.toFixed(1)}%
                            </span>
                        </div>
                    )}
                </div>

                {/* Lote aberto warning */}
                {selected && selected.status !== 'closed' && (
                    <div className="flex items-start gap-2 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-3">
                        <FiAlertCircle className="text-amber-600 mt-0.5 flex-shrink-0" size={16} />
                        <div className="text-sm text-amber-800 dark:text-amber-300">
                            <strong>Lote aberto:</strong> NFes podem entrar ou sair desta operação. Você poderá re-sincronizar quando o lote for fechado.
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default NewAnalysisFromOperation;
